<template>
    <div class="itf">
        <div class="itf-head">
            <h3 class="card-title">{{ tip && tip.id ? 'Modifier la piste' : 'Nouvelle piste' }}</h3>
            <el-dropdown trigger="click" @command="useTemplate">
                <el-button size="small">Partir d'un modèle <i class="el-icon-arrow-down el-icon--right"></i></el-button>
                <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item v-for="(t, k) in templates" :key="k" :command="k">{{ t.name }}</el-dropdown-item>
                </el-dropdown-menu>
            </el-dropdown>
        </div>

        <el-alert v-if="errors.length" type="error" :closable="false" title="La piste n'a pas été enregistrée" class="mb-3">
            <ul class="itf-errors"><li v-for="(e, i) in errors" :key="i">{{ e }}</li></ul>
        </el-alert>

        <div class="row">
            <div class="col-lg-7">
                <div class="card"><div class="card-body">
                    <h4 class="itf-step">1. Sur quelles lignes de la recette ?</h4>
                    <div v-for="key in activeCriteria" :key="key" class="itf-crit" role="group" :aria-labelledby="'crit-label-' + key">
                        <label :id="'crit-label-' + key">{{ criteriaLabels[key] }}</label>
                        <div class="itf-crit-row">
                            <el-select v-if="key === 'ingredient_ids'" v-model="form.target.ingredient_ids" multiple filterable remote
                                       :remote-method="searchIngredients" :loading="searching" placeholder="Rechercher un ingrédient" class="itf-grow">
                                <el-option v-for="o in ingredientOptions(form.target.ingredient_ids)" :key="o.value" :label="o.label" :value="o.value"></el-option>
                            </el-select>
                            <el-select v-else v-model="form.target[key]" multiple filterable placeholder="Choisir" class="itf-grow">
                                <el-option v-for="o in options[key]" :key="o.value" :label="o.label" :value="o.value"></el-option>
                            </el-select>
                            <el-button type="text" icon="el-icon-delete" :aria-label="'Retirer le critère ' + criteriaLabels[key]" @click="removeCriterion(key)"></el-button>
                        </div>
                    </div>
                    <p v-if="!activeCriteria.length" class="text-muted">Ajoutez au moins un critère : un groupe (ex. viande rouge), un type, une origine…</p>
                    <el-dropdown v-if="availableCriteria.length" trigger="click" @command="addCriterion">
                        <el-button size="small" icon="el-icon-plus">Ajouter un critère</el-button>
                        <el-dropdown-menu slot="dropdown">
                            <el-dropdown-item v-for="key in availableCriteria" :key="key" :command="key">{{ criteriaLabels[key] }}</el-dropdown-item>
                        </el-dropdown-menu>
                    </el-dropdown>
                    <div class="itf-scope">
                        <el-radio-group v-model="form.target.scope">
                            <el-radio label="all">Toutes ces lignes</el-radio>
                            <el-radio label="top">Seulement la plus impactante</el-radio>
                        </el-radio-group>
                    </div>
                </div></div>

                <div class="card"><div class="card-body">
                    <h4 class="itf-step">2. Que proposer ?</h4>
                    <el-radio-group v-model="form.action.type" class="itf-actions">
                        <el-radio v-for="(label, type) in actionLabels" :key="type" :label="type" border>{{ label }}</el-radio>
                    </el-radio-group>

                    <div v-if="form.action.type === 'replace'" class="itf-params">
                        <label for="act-ingredient">Remplacer par</label>
                        <el-select id="act-ingredient" v-model="form.action.ingredient_id" filterable remote :remote-method="searchIngredients" :loading="searching" placeholder="Rechercher un ingrédient" class="itf-grow">
                            <el-option v-for="o in ingredientOptions([form.action.ingredient_id])" :key="o.value" :label="o.label" :value="o.value"></el-option>
                        </el-select>
                        <label>Quantité</label>
                        <div>
                            <el-radio-group v-model="ratioMode">
                                <el-radio label="equal">À poids égal</el-radio>
                                <el-radio label="custom">Multiplier le poids par</el-radio>
                            </el-radio-group>
                            <el-input-number v-if="ratioMode === 'custom'" v-model="form.action.ratio" :min="0.1" :max="10" :step="0.1" size="small" label="Coefficient de quantité"></el-input-number>
                        </div>
                    </div>

                    <div v-if="form.action.type === 'reduce'" class="itf-params">
                        <label>Réduire la quantité de</label>
                        <div><el-input-number v-model="form.action.percent" :min="1" :max="100" size="small" label="Réduire la quantité de"></el-input-number> %</div>
                        <label for="act-compensate">Compenser avec (facultatif)</label>
                        <el-select id="act-compensate" v-model="form.action.compensate_ingredient_id" clearable filterable remote :remote-method="searchIngredients" :loading="searching" placeholder="Aucune compensation" class="itf-grow">
                            <el-option v-for="o in ingredientOptions([form.action.compensate_ingredient_id])" :key="o.value" :label="o.label" :value="o.value"></el-option>
                        </el-select>
                        <template v-if="form.action.compensate_ingredient_id">
                            <label>Part de la quantité retirée</label>
                            <div><el-input-number v-model="form.action.compensate_ratio" :min="0.1" :max="10" :step="0.1" size="small" label="Part de la quantité retirée"></el-input-number>
                                <span class="text-muted small">1 = autant, 0,5 = la moitié</span></div>
                        </template>
                    </div>

                    <div v-if="valueOptionsKey" class="itf-params">
                        <label for="act-value">Nouvelle valeur</label>
                        <el-select id="act-value" v-model="form.action.value" filterable placeholder="Choisir" class="itf-grow">
                            <el-option v-for="o in options[valueOptionsKey].filter(v => v.value !== 'none')" :key="o.value" :label="o.label" :value="o.value"></el-option>
                        </el-select>
                    </div>
                </div></div>

                <div class="card"><div class="card-body">
                    <h4 class="itf-step">3. Comment l'annoncer ?</h4>
                    <label for="tip-title">Titre (à l'impératif)</label>
                    <el-input id="tip-title" ref="title" v-model="form.title" maxlength="255" @focus="focused = 'title'"></el-input>
                    <label for="tip-description" class="mt-2">Explication, effets de bord compris</label>
                    <el-input id="tip-description" ref="description" v-model="form.description" type="textarea" :rows="3" maxlength="1000" @focus="focused = 'description'"></el-input>
                    <div class="itf-vars">
                        Insérer :
                        <button v-for="v in variables" :key="v.code" type="button" class="itf-chip" :title="v.help" @mousedown.prevent @click="insertVar(v.code)">{{ v.code }}</button>
                    </div>
                    <p v-for="w in warnings" :key="w" class="text-warning small mb-1">{{ w }}</p>
                    <el-checkbox v-model="form.active" class="mt-2">Piste active (proposée dans le calculateur)</el-checkbox>
                    <div class="mt-3">
                        <el-button type="primary" :loading="saving" @click="save">Enregistrer</el-button>
                        <a :href="indexUrl" class="btn btn-link">Annuler</a>
                    </div>
                </div></div>
            </div>

            <div class="col-lg-5">
                <div class="card itf-sticky"><div class="card-body">
                    <h4 class="itf-step">Aperçu dans le calculateur</h4>
                    <div class="itf-tip"><span class="n">1</span><p><b>{{ previewText(form.title) || 'Titre de la piste' }}</b><span v-if="form.description" class="d">{{ previewText(form.description) }}</span></p></div>
                    <p class="text-muted small">Exemple avec deux lignes visées.</p>
                    <hr>
                    <el-button size="small" :loading="testing" @click="test(null)">Tester sur les 2 000 dernières recettes</el-button>
                    <div class="itf-test-one">
                        <el-input v-model="testRecipeId" size="small" placeholder="N° de recette" label="Numéro de recette à tester"></el-input>
                        <el-button size="small" :loading="testing" :disabled="!testRecipeId" @click="test(testRecipeId)">Tester sur cette recette</el-button>
                    </div>
                    <div v-if="result" class="mt-3" role="status">
                        <p v-if="recipeNotFound" class="mb-1">Recette introuvable.</p>
                        <template v-else>
                            <p class="mb-1"><b>Se déclencherait sur {{ result.count }} recette{{ result.count > 1 ? 's' : '' }}</b>
                                sur {{ result.total }} {{ result.total === 1 ? 'testée' : 'modifiées récemment' }}.</p>
                            <p v-if="form.target.scope === 'top'" class="text-muted small">Ici, « la plus impactante » est estimée par le Kg CO2 de l'ingrédient × la quantité ; dans le calculateur, par l'impact réel de la ligne.</p>
                            <div v-for="e in result.examples" :key="e.recipe_id" class="itf-example">
                                <a :href="e.url" target="_blank">{{ e.name }}</a>
                                <div class="small"><b>{{ e.title }}</b></div>
                                <ul class="small"><li v-for="(c, i) in e.changes" :key="i">{{ c }}</li></ul>
                            </div>
                        </template>
                    </div>
                </div></div>
            </div>
        </div>
    </div>
</template>

<script>
const CRITERIA = ['ingredient_ids', 'type_slugs', 'nutriscore_fl', 'provenances', 'conservations', 'labels'];
const IMPORTED = ['pays_limitrophe', 'pays_limitrophe_et_circuit_court', 'reste_du_monde', 'reste_du_monde_et_circuit_court',
    'espagne', 'union_europeenne', 'amerique_du_nord', 'asie', 'amerique_du_sud', 'afrique'];

export default {
    name: 'ImprovementTipForm',
    props: {
        tip: {type: Object, default: null},
        options: {type: Object, required: true},
        ingredientNames: {type: Object, default: () => ({})},
        saveUrl: {type: String, required: true},
        saveMethod: {type: String, default: 'post'},
        previewUrl: {type: String, required: true},
        indexUrl: {type: String, required: true},
    },
    data() {
        const tip = this.tip || {};
        const target = Object.assign({scope: 'all'}, tip.target || {});
        return {
            form: {
                title: tip.title || '',
                description: tip.description || '',
                active: !!tip.active,
                target,
                action: Object.assign({type: 'replace', ratio: 1}, tip.action || {}),
            },
            activeCriteria: CRITERIA.filter((k) => Array.isArray(target[k])),
            names: Object.assign({}, this.ingredientNames),
            searchResults: [],
            searching: false,
            focused: 'title',
            errors: [],
            saving: false,
            testing: false,
            testRecipeId: '',
            // Mode du dernier test lancé (recette précise ou non), indépendant du champ testRecipeId encore modifiable ensuite
            lastTestedRecipeId: null,
            result: null,
            criteriaLabels: {
                ingredient_ids: 'Ingrédients précis', type_slugs: "Type d'ingrédient", nutriscore_fl: 'Groupe Nutri-score',
                provenances: 'Origine', conservations: 'Conservation', labels: 'Label',
            },
            actionLabels: {
                replace: "Remplacer l'ingrédient", reduce: 'Réduire la quantité', set_provenance: "Changer l'origine",
                set_conservation: 'Changer la conservation', set_label: 'Changer le label',
            },
            variables: [
                {code: '{n}', help: 'Nombre de lignes visées'},
                {code: '{s}', help: '« s » si plusieurs lignes'},
                {code: '{ingredients}', help: 'Noms des ingrédients visés'},
                {code: '{remplacement}', help: "Ingrédient de remplacement ou de compensation"},
            ],
            templates: [
                {name: 'Remplacer la viande la plus impactante', target: {scope: 'top', type_slugs: ['viande-oeufs-laitages']},
                    action: {type: 'replace', ingredient_id: null, ratio: 1}, title: 'Remplacer {ingredients} par {remplacement}',
                    description: "Substitution à poids égal. Surveillez le Nutri-score : ces produits sont plus salés, mais l'effet dépend du reste de la recette."},
                {name: 'Réduire la viande rouge de 40 %', target: {scope: 'all', nutriscore_fl: ['VR']},
                    action: {type: 'reduce', percent: 40, compensate_ingredient_id: null, compensate_ratio: 0.5}, title: 'Réduire de 40 % la portion de {ingredients}',
                    description: 'Compensée en {remplacement} à poids sec équivalent.'},
                {name: 'Passer en bio les végétaux sans label', target: {scope: 'all', type_slugs: ['fruit-legume-herbes', 'autres'], labels: ['none']},
                    action: {type: 'set_label', value: 'bio'}, title: 'Passer en bio {n} produit{s} sans label',
                    description: 'Le bonus labels est le plus fort levier du Green-score.'},
                {name: 'Sourcer en France les ingrédients importés', target: {scope: 'all', provenances: IMPORTED.slice()},
                    action: {type: 'set_provenance', value: 'national'}, title: 'Sourcer en France {n} ingrédient{s} importé{s}',
                    description: "Le malus d'origine disparaît et le bonus provenance augmente."},
                {name: 'Repasser en frais les fruits et légumes', target: {scope: 'all', type_slugs: ['fruit-legume-herbes'], conservations: ['en_conserve', 'congele']},
                    action: {type: 'set_conservation', value: 'refrigere'}, title: 'Repasser en frais {n} fruit{s} ou légume{s} surgelé{s} ou en conserve',
                    description: 'Le malus de conservation tombe et le bonus de saison redevient accessible.'},
                {name: 'Remplacer une espèce menacée', target: {scope: 'all', ingredient_ids: []},
                    action: {type: 'replace', ingredient_id: null, ratio: 1}, title: 'Remplacer {ingredients} par une espèce non menacée',
                    description: 'Le malus espèce menacée est forfaitaire : moins dix points, quelle que soit la quantité.'},
            ],
        };
    },
    computed: {
        availableCriteria() {
            return CRITERIA.filter((k) => this.activeCriteria.indexOf(k) === -1);
        },
        valueOptionsKey() {
            return {set_provenance: 'provenances', set_conservation: 'conservations', set_label: 'labels'}[this.form.action.type] || null;
        },
        ratioMode: {
            get() { return Number(this.form.action.ratio) === 1 ? 'equal' : 'custom'; },
            set(mode) { this.$set(this.form.action, 'ratio', mode === 'equal' ? 1 : 0.5); },
        },
        replacementName() {
            const a = this.form.action;
            const id = a.type === 'replace' ? a.ingredient_id : (a.type === 'reduce' ? a.compensate_ingredient_id : null);
            return id ? (this.names[id] || '').toLowerCase() : '';
        },
        // Test sur une recette précise sans résultat : recette inexistante plutôt que « 0 sur 1 testée »
        recipeNotFound() {
            return !!this.lastTestedRecipeId && !!this.result && this.result.total === 0;
        },
        warnings() {
            const text = this.form.title + ' ' + (this.form.description || '');
            const out = [];
            if (text.indexOf('{remplacement}') !== -1 && !this.replacementName) {
                out.push("{remplacement} restera vide : choisissez un ingrédient de remplacement ou de compensation.");
            }
            return out;
        },
    },
    methods: {
        addCriterion(key) {
            if (!Array.isArray(this.form.target[key])) this.$set(this.form.target, key, []);
            this.activeCriteria.push(key);
        },
        removeCriterion(key) {
            this.activeCriteria = this.activeCriteria.filter((k) => k !== key);
            this.$delete(this.form.target, key);
        },
        searchIngredients(q) {
            if (!q) return;
            this.searching = true;
            axios.get('/ingredients', {params: {q, autocomplete: true, order: 'label', perPage: 40}}).then((r) => {
                this.searchResults = (r.data.data || []).map((i) => ({value: i.id, label: i.label}));
                this.searchResults.forEach((o) => this.$set(this.names, o.value, o.label));
            }).finally(() => { this.searching = false; });
        },
        // Résultats de la recherche + valeurs déjà choisies (pour garder leur libellé)
        ingredientOptions(selected) {
            const out = this.searchResults.slice();
            (selected || []).filter((id) => id && !out.some((o) => o.value === id))
                .forEach((id) => out.push({value: id, label: this.names[id] || ('#' + id)}));
            return out;
        },
        useTemplate(k) {
            const t = JSON.parse(JSON.stringify(this.templates[k]));
            this.form.target = t.target;
            this.form.action = t.action;
            this.form.title = t.title;
            this.form.description = t.description;
            this.activeCriteria = CRITERIA.filter((c) => Array.isArray(t.target[c]));
            this.result = null;
        },
        insertVar(code) {
            const field = this.focused;
            const el = this.$refs[field].$refs[field === 'title' ? 'input' : 'textarea'];
            const value = this.form[field] || '';
            const start = typeof el.selectionStart === 'number' ? el.selectionStart : value.length;
            const end = typeof el.selectionEnd === 'number' ? el.selectionEnd : value.length;
            this.form[field] = value.slice(0, start) + code + value.slice(end);
            this.$nextTick(() => { el.focus(); el.setSelectionRange(start + code.length, start + code.length); });
        },
        // Rendu d'exemple : deux lignes visées
        previewText(text) {
            const ids = this.form.target.ingredient_ids || [];
            const names = ids.slice(0, 2).map((id) => (this.names[id] || '').toLowerCase()).filter(Boolean);
            const ingredients = names.length === 2 ? names.join(' et ') : (names[0] ? names[0] + ' et un autre ingrédient' : 'ingrédient A et ingrédient B');
            return (text || '').split('{n}').join('2').split('{s}').join('s').split('{ingredients}').join(ingredients)
                .split('{remplacement}').join(this.replacementName || "l'ingrédient choisi");
        },
        test(recipeId) {
            this.testing = true;
            this.lastTestedRecipeId = recipeId || null;
            axios.post(this.previewUrl, Object.assign({}, this.form, {recipe_id: recipeId || null})).then((r) => {
                this.result = r.data;
            }).catch(() => {
                this.result = null;
                this.$message({type: 'error', message: "Test impossible : vérifiez qu'une action est choisie."});
            }).finally(() => { this.testing = false; });
        },
        save() {
            this.saving = true;
            this.errors = [];
            axios[this.saveMethod](this.saveUrl, this.form).then((r) => {
                window.location = r.data.redirect;
            }).catch((e) => {
                const errors = e.response && e.response.status === 422 ? e.response.data.errors : null;
                this.errors = errors ? [].concat.apply([], Object.values(errors)) : ['Erreur inattendue, merci de réessayer.'];
                window.scrollTo(0, 0);
            }).finally(() => { this.saving = false; });
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~bootstrap/scss/functions';
@import '../../sass/variables';

.itf-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }
.itf-errors { margin: 0; padding-left: 18px; }
.itf-step { font-size: 1rem; font-weight: 700; margin-bottom: .75rem; }
.itf-crit { margin-bottom: .75rem; label { display: block; font-weight: 600; margin-bottom: .25rem; } }
.itf-crit-row { display: flex; gap: 8px; align-items: center; }
.itf-grow { flex: 1; width: 100%; }
.itf-scope { margin-top: 1rem; }
.itf-actions { display: flex; flex-wrap: wrap; gap: 8px; .el-radio { margin: 0; } }
.itf-params { display: grid; grid-template-columns: 200px 1fr; gap: 10px 14px; align-items: center; margin-top: 1rem;
    label { font-weight: 600; margin: 0; } }
.itf-vars { margin-top: .5rem; font-size: 13px; }
.itf-chip { border: 1px solid $calc-line-2; background: $calc-field; border-radius: 99px; padding: 1px 9px; margin: 0 3px; font-family: monospace; cursor: pointer;
    &:hover { border-color: $calc-teal; } }
.itf-sticky { position: sticky; top: 16px; }
.itf-tip { display: flex; gap: 11px; align-items: flex-start; padding: 11px 13px; border-radius: 11px; background: $calc-coral-soft;
    border: 1px solid mix($calc-coral-l, $calc-coral-soft, 35%); color: $calc-ink;
    .n { flex: 0 0 auto; width: 24px; height: 24px; border-radius: 99px; background: $calc-coral; color: $calc-surface; font-weight: 700; font-size: 12.5px; display: grid; place-items: center; }
    p { margin: 0; font-size: 13px; line-height: 1.45; }
    .d { display: block; color: $calc-ink-2; } }
.itf-test-one { display: flex; gap: 8px; margin-top: 8px; .el-input { width: 140px; } }
.itf-example { border-top: 1px solid $calc-line; padding-top: 8px; margin-top: 8px; ul { margin: 4px 0 0; padding-left: 18px; } }
@media (max-width: 760px) { .itf-params { grid-template-columns: 1fr; } }
</style>
